import * as React from 'react';
import { FC, useCallback } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { isNumber } from 'lodash/fp';
import { isUndefined, noop } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import Animation from 'components/lottie-animations/favorite-loading/favorite-loading';
import { CustomDisclaimer } from 'components/offers/ui-refresh/offer-disclaimer';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { useForceStoreSelectionFlagged } from 'hooks/use-force-store-selection';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import OfferLocked from 'pages/loyalty/offer-locked/offer-locked';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';

import { DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE } from '../constants';
import { DefaultBackground, PointsNeededText } from '../incentive-card/incentive-card.styled';

import {
  POINTS_TRANSLATION_KEY,
  REDEEM_REWARD_TRANSLATION_KEY,
  SHOULD_DISPLAY_ICON,
} from './constants';
import {
  BackgroundImage,
  ButtonContainer,
  ButtonsWrapper,
  DisclaimerText,
  ErrorMessage,
  ImageWrapper,
  LoadingWrapper,
  Padding,
  RemoveRewardsButton,
  RewardDescription,
  RewardDetailsContainer,
  RewardName,
  StyledActionButton,
  StyledDisclaimerContainer,
  StyledIncentiveEvaluationMessages,
  StyledProgressMeter,
  StyledRewardImage,
  StyledSecondaryActionButton,
  Wrapper,
  WrapperButton,
} from './incentive-details.styled';
import { RewardPoints } from './reward-points';
import { IIncentiveDetailsViewProps } from './types';

export const IncentiveDetailsView: FC<IIncentiveDetailsViewProps> = ({
  id,
  name,
  image,
  description,
  moreInfo,
  isOffer,
  pointCost,
  locked,
  remainingPointsNeededForRedemption,
  redemptionPercentage,
  handleRedeemRewardClick,
  handleInRestaurantRedemptionClick,
  errorMessage,
  checkingAvailability,
  backgroundImage,
  canAddToInRestaurantOrder = false,
  canAddToMobileOrder = false,
  showRemoveRewardButton = false,
  handleRemoveRewardsClick,
  incentiveEvaluationResults,
  offersInCooldown = false,
  favorited = false,
  favoriteLoading = false,
  handleToggleFavorite = noop,
  challengeIncentiveSelected = false,
}) => {
  const { formatMessage } = useIntl();
  useForceStoreSelectionFlagged();
  const { isAuthenticated } = useAuthContext();

  const enableMyCodeNewUI = Boolean(useFlag(LaunchDarklyFlag.ENABLE_MY_CODE_NEW_UI));
  const meterEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_METER_ON_REWARDS));
  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

  const { featureInvitationCode } = useFeatureInvitationCode();

  const questBenefit =
    featureInvitationCode?.invitationCodeBenefit && featureInvitationCode?.invitationCodeBenefit[0];

  const incentiveDetails = {
    name: challengeIncentiveSelected ? questBenefit?.name?.locale : name,
    description: challengeIncentiveSelected ? questBenefit?.howToRedeem?.localeRaw : description,
    image: challengeIncentiveSelected ? questBenefit?.image?.locale : image,
    backgroundImage: <DefaultBackground />,
  };

  // If not a number the incentive is an offer in other way it's a reward
  const redeemText = !isNumber(pointCost)
    ? formatMessage({ id: 'addMobileOrder' })
    : `${formatMessage({ id: REDEEM_REWARD_TRANSLATION_KEY })}`;

  const inRestaurantRedemptionText = formatMessage({
    id: enableMyCodeNewUI ? 'redeemAtRestaurant' : 'redeemInRestaurant',
  });

  const hasServiceModeError = (
    evaluationResults: IIncentiveDetailsViewProps['incentiveEvaluationResults']
  ) =>
    !!evaluationResults?.some(
      evaluationResult =>
        evaluationResult.code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );

  const handleFavoriteClick = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      handleToggleFavorite(id, name);
    },
    [handleToggleFavorite, id, name]
  );

  const hasBlockingErrors =
    !!errorMessage ||
    (offersInCooldown && isOffer) ||
    // Disable buttons if Feedback is not included in fixable incentive errors
    incentiveEvaluationResults?.some(
      evaluationResult =>
        !FixableEvaluationErrors.has(evaluationResult.code as IncentiveEvaluationErrorCodes)
    );

  const disableMobileOrderButton =
    hasBlockingErrors ||
    hasServiceModeError(incentiveEvaluationResults) ||
    challengeIncentiveSelected;

  const disableInRestaurantOrderButton =
    hasBlockingErrors ||
    (DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE &&
      hasServiceModeError(incentiveEvaluationResults)) ||
    challengeIncentiveSelected;

  return (
    <Wrapper>
      <ImageWrapper>
        {enableFavoriteOffers && isAuthenticated() && isOffer && (
          <WrapperButton>
            <ButtonForIconFav
              data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
              onClick={handleFavoriteClick}
              aria-label={formatMessage({
                id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
              })}
              isTransparent={false}
            >
              {favoriteLoading ? (
                <LoadingWrapper>
                  <Animation />
                </LoadingWrapper>
              ) : (
                <FavoriteIcon isFavorite={favorited} />
              )}
            </ButtonForIconFav>
          </WrapperButton>
        )}

        {challengeIncentiveSelected
          ? incentiveDetails.backgroundImage
          : backgroundImage && (
              <BackgroundImage
                alt={name}
                image={backgroundImage}
                isChallengeIncentive={challengeIncentiveSelected}
              />
            )}
        {incentiveDetails.image && (
          <StyledRewardImage
            alt={name}
            image={incentiveDetails.image}
            objectFitContain
            isChallengeIncentive={challengeIncentiveSelected}
          />
        )}
        {challengeIncentiveSelected && <OfferLocked />}
      </ImageWrapper>
      <RewardDetailsContainer>
        {!isUndefined(pointCost) && <RewardPoints pointCost={pointCost} locked={Boolean(locked)} />}
        {incentiveDetails.name && <RewardName>{incentiveDetails.name}</RewardName>}
        {incentiveDetails.description && (
          <RewardDescription content={incentiveDetails.description} />
        )}
        {locked && (
          <>
            {meterEnabled && isNumber(redemptionPercentage) && (
              <StyledProgressMeter percentage={redemptionPercentage} />
            )}
            {isNumber(remainingPointsNeededForRedemption) && (
              <PointsNeededText>
                {formatMessage(
                  { id: 'yourXPointsAway' },
                  {
                    points: <span>{remainingPointsNeededForRedemption}</span>,
                    pointsSlang: POINTS_TRANSLATION_KEY,
                  }
                )}
              </PointsNeededText>
            )}
          </>
        )}
        {!isOffer && moreInfo && <DisclaimerText content={moreInfo} />}
        {/* padding element needed for safari / iOS native devices */}
        <Padding />
      </RewardDetailsContainer>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <ButtonsWrapper>
        {!locked && (
          <ButtonContainer>
            {canAddToMobileOrder && (
              <StyledActionButton
                disabled={disableMobileOrderButton}
                onClick={handleRedeemRewardClick}
                isLoading={checkingAvailability}
                data-testid="redeem-to-mobile-order-btn"
                startIcon={
                  SHOULD_DISPLAY_ICON ? (
                    <Icon icon={'mobile'} color={'white'} width="24px" aria-hidden />
                  ) : null
                }
              >
                {redeemText}
              </StyledActionButton>
            )}

            {canAddToInRestaurantOrder && (
              <StyledSecondaryActionButton
                disabled={disableInRestaurantOrderButton}
                onClick={handleInRestaurantRedemptionClick}
                isLoading={checkingAvailability}
                startIcon={
                  SHOULD_DISPLAY_ICON ? (
                    <Icon icon={'storeTwo'} color={'primary'} width="24px" aria-hidden />
                  ) : null
                }
                variant={
                  canAddToMobileOrder
                    ? ActionButtonVariants.TEXT_ONLY
                    : ActionButtonVariants.OUTLINE
                }
              >
                {inRestaurantRedemptionText}
              </StyledSecondaryActionButton>
            )}
          </ButtonContainer>
        )}

        {showRemoveRewardButton && (
          <ButtonContainer>
            <RemoveRewardsButton
              onClick={handleRemoveRewardsClick}
              variant={ActionButtonVariants.OUTLINE}
            >
              {formatMessage({ id: 'removeRewardsFromCart' })}
            </RemoveRewardsButton>
          </ButtonContainer>
        )}
      </ButtonsWrapper>

      {incentiveEvaluationResults && (
        <StyledIncentiveEvaluationMessages
          incentiveEvaluationResults={incentiveEvaluationResults}
          hideIcon
        />
      )}
      {isOffer && moreInfo && (
        <StyledDisclaimerContainer>
          <CustomDisclaimer content={moreInfo} />
        </StyledDisclaimerContainer>
      )}

      {offersInCooldown && <LoyaltyOffersCooldownDrawer />}
    </Wrapper>
  );
};
